import React, { useState } from "react";
import { League } from "../types/league";
import {
  Users,
  Calendar,
  TrendingUp,
  ArrowLeft,
  CheckCircle,
  XCircle,
} from "lucide-react";

interface GenericLeagueDashboardProps {
  league: League;
  setLeague: (league: League) => void;
  onReset: () => void;
}

const GenericLeagueDashboard: React.FC<GenericLeagueDashboardProps> = ({
  league,
  setLeague,
  onReset,
}) => {
  const [activeView, setActiveView] = useState<
    "overview" | "teams" | "stats" | "matches"
  >("overview");
  const [selectedMatch, setSelectedMatch] = useState<any>(null);

  const getLeagueTypeName = (type: string) => {
    switch (type) {
      case "ladder":
        return "Ladder League";
      case "hybrid":
        return "Hybrid Progressive League";
      case "king_of_court":
        return "King of the Court League";
      default:
        return "League";
    }
  };

  const getLeagueTypeDescription = (type: string) => {
    switch (type) {
      case "ladder":
        return "Continuous challenge-based rankings with flexible scheduling";
      case "hybrid":
        return "Multi-phase competition: Pool Play → Ladder → Playoffs";
      case "king_of_court":
        return "Session-based court challenges with points system";
      default:
        return "League competition";
    }
  };

  // Helper function to get teams count based on league type
  const getTeamsCount = () => {
    if (league.leagueType === "division") {
      return (
        (league as any).divisions?.reduce(
          (total: number, div: any) => total + div.teams.length,
          0
        ) || 0
      );
    }
    return (league as any).teams?.length || 0;
  };

  // Helper function to get teams array based on league type
  const getTeams = () => {
    if (league.leagueType === "division") {
      return (league as any).divisions?.flatMap((div: any) => div.teams) || [];
    }
    return (league as any).teams || [];
  };

  const handleEnterScore = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    // This is a simplified implementation - in a real app, you'd update the specific league type
    console.log(
      `Score entered for match ${matchId}: ${team1Score}-${team2Score}`
    );
    setSelectedMatch(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={onReset}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {league.name}
                </h1>
                <p className="text-gray-600">
                  {getLeagueTypeName(league.leagueType)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Teams</p>
                <p className="font-semibold">{getTeamsCount()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: "overview", name: "Overview", icon: Users },
              { id: "teams", name: "Teams", icon: Users },
              { id: "matches", name: "Matches", icon: Calendar },
              { id: "stats", name: "Statistics", icon: TrendingUp },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id as any)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeView === item.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview View */}
        {activeView === "overview" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center">
                <div className="mb-4">
                  <Users className="w-16 h-16 text-blue-600 mx-auto" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {getLeagueTypeName(league.leagueType)}
                </h2>
                <p className="text-gray-600 mb-6">
                  {getLeagueTypeDescription(league.leagueType)}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">
                      {getTeamsCount()}
                    </div>
                    <div className="text-sm text-blue-600">Teams</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {league.stats.totalMatches}
                    </div>
                    <div className="text-sm text-green-600">Total Matches</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">
                      {league.stats.completedMatches}
                    </div>
                    <div className="text-sm text-purple-600">Completed</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  League Information
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      League Details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">League Type:</span>
                        <span className="font-medium">
                          {getLeagueTypeName(league.leagueType)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className="font-medium">
                          {league.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Created:</span>
                        <span className="font-medium">
                          {league.createdAt.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Updated:</span>
                        <span className="font-medium">
                          {league.updatedAt.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      League Statistics
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Matches:</span>
                        <span className="font-medium">
                          {league.stats.totalMatches}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Completed Matches:
                        </span>
                        <span className="font-medium">
                          {league.stats.completedMatches}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Perfect Games:</span>
                        <span className="font-medium">
                          {league.stats.perfectGames}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Points/Game:</span>
                        <span className="font-medium">
                          {league.stats.averagePointsPerGame.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Teams View */}
        {activeView === "teams" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  League Teams
                </h2>
                <p className="text-sm text-gray-600">
                  {getTeamsCount()} teams participating
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {getTeams().map((team: any, index: number) => (
                  <div key={team.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {index + 1}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {team.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {team.player1} & {team.player2}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {team.wins}W - {team.losses}L
                        </div>
                        <div className="text-xs text-gray-500">
                          {team.wins + team.losses > 0
                            ? `${(
                                (team.wins / (team.wins + team.losses)) *
                                100
                              ).toFixed(1)}%`
                            : "0%"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Matches View */}
        {activeView === "matches" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  League Matches
                </h2>
                <p className="text-sm text-gray-600">
                  {getLeagueTypeName(league.leagueType)} match management
                </p>
              </div>
              <div className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">
                    {getLeagueTypeName(league.leagueType)} Matches
                  </p>
                  <p className="text-sm">
                    Match management for{" "}
                    {getLeagueTypeName(league.leagueType).toLowerCase()}. Click
                    "Add Match" to create new matches or click on existing
                    matches to enter scores.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics View */}
        {activeView === "stats" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Total Teams
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {getTeamsCount()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Calendar className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Total Matches
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.stats.totalMatches}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Completed
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.stats.completedMatches}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  League Statistics
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Match Statistics
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Matches:</span>
                        <span className="font-medium">
                          {league.stats.totalMatches}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Completed Matches:
                        </span>
                        <span className="font-medium">
                          {league.stats.completedMatches}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Completion Rate:</span>
                        <span className="font-medium">
                          {league.stats.totalMatches > 0
                            ? `${(
                                (league.stats.completedMatches /
                                  league.stats.totalMatches) *
                                100
                              ).toFixed(1)}%`
                            : "0%"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Average Points/Game:
                        </span>
                        <span className="font-medium">
                          {league.stats.averagePointsPerGame.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Performance Statistics
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Perfect Games:</span>
                        <span className="font-medium">
                          {league.stats.perfectGames}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Most Wins:</span>
                        <span className="font-medium">
                          {league.stats.mostWins?.name || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Most Points:</span>
                        <span className="font-medium">
                          {league.stats.mostPoints?.name || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Longest Streak:</span>
                        <span className="font-medium">
                          {league.stats.longestStreak?.name || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Score Entry Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Enter Match Score
              </h3>
              <button
                onClick={() => setSelectedMatch(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedMatch.team1?.name || "Team 1"} Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="13"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0-13"
                  id="team1Score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedMatch.team2?.name || "Team 2"} Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="13"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0-13"
                  id="team2Score"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="text-sm text-blue-800">
                  <strong>Pickleball Rules:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• First to 11 points wins</li>
                    <li>• Must win by 2 points</li>
                    <li>• Maximum score is 13 points</li>
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedMatch(null)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const team1Score = parseInt(
                      (
                        document.getElementById(
                          "team1Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    const team2Score = parseInt(
                      (
                        document.getElementById(
                          "team2Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    if (
                      !isNaN(team1Score) &&
                      !isNaN(team2Score) &&
                      team1Score >= 0 &&
                      team1Score <= 13 &&
                      team2Score >= 0 &&
                      team2Score <= 13 &&
                      team1Score !== team2Score
                    ) {
                      handleEnterScore(
                        selectedMatch.id,
                        team1Score,
                        team2Score
                      );
                    }
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Submit Score
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericLeagueDashboard;
